/**
 * Progress — who is getting through this academy's courses, and who has not started.
 *
 * NOT one course at a time, which is where this page started and why it was wrong. It opened on
 * a dropdown and an empty panel, so the question it is opened with — "is anybody actually doing
 * this?" — could not be asked at all without first guessing a course.
 *
 * THE LIST IS ONE REQUEST TO `academy/progress`, where it used to be the worst page in the
 * estate: up to 60 calls to `/api/course_home/v1/progress/{course}/{user_id}/`, one per learner
 * per course, capped at 60 so a bigger academy silently lost the rest — MEASURED at 108.08s on
 * the VPS, with a single worst request of 81.135s. The replacement reads the completion
 * aggregator directly: two queries serve the whole page regardless of how many learner-course
 * pairs are on it (one for the enrolments, one for the `Aggregator` rows), which is why there is
 * no cap left to report — see `academy.py::read_progress` for the two-query story.
 *
 * WHAT THE BULK ROW CANNOT SAY, AND DID NOT USED TO EITHER. `percent`, `earned` and `possible`
 * are the aggregator's own completion quantities, not the courseware-outline concepts
 * (`completion_summary`'s complete/incomplete/locked unit COUNTS, `course_grade`,
 * `section_scores`, `certificate_data`) that `/api/course_home/v1/progress/…` answers per
 * learner. Reproducing those here would mean going back to one request per row — exactly what
 * this replaces — so the "Scores" drill-in still reaches `instructor.ts::getLearnerProgress` for
 * that detail, ON DEMAND for the one row an admin opens, rather than eagerly for every row on the
 * page. One consequence of that split: the table below can no longer show a grade column, because
 * the bulk read has no grade in it at all.
 *
 * NO "NOT AVAILABLE" STAGE ANY MORE, either, and for the same reason. The old per-learner
 * endpoint made one HTTP-shaped request per row, and a course that had not opened yet answered it
 * with a 403 that the row kept as `reason` rather than being dropped. The bulk read is two
 * database queries for the WHOLE page, not one attempt per enrolment, so there is no per-row
 * failure left to report — every row that comes back has a percent, so every row sorts into one
 * of the three real stages.
 *
 * WHAT THIS PAGE IS NOT SHOWING, and did not before either: when somebody was last active. No
 * built-in endpoint carries it — `/api/completion/v1/` exposes only one subsection at a time for
 * one learner, and a batch endpoint that only writes. So "stalled" is not asserted. What is shown
 * instead is honest and nearly as useful: nothing started, and the date they enrolled.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Banner,
  Button,
  DataTable,
  Kpi,
  KpiRow,
  Pill,
  SearchField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { listAcademyProgress, type AcademyCourse, type AcademyProgress } from '../../shared/lib/academy';
import { asPercent, completionShare, getLearnerProgress, type LearnerProgress } from '../../shared/lib/instructor';
import { PageHeader } from '../AdminShell';
import { CourseCell, CourseSwitcher, courseChoices, useCourseParam } from '../CourseFilter';
import { useAcademyCourses } from '../useAcademyCourses';

type Stage = 'all' | 'not-started' | 'underway' | 'finished';

/** Who is open in the detail panel below the toolbar. */
interface Open {
  courseId: string;
  userId: number;
}

/**
 * `percent === 0` is "not started" and `percent >= 1` is "finished" — the same two facts the old
 * classification read off `complete_count === 0` and `incomplete_count === 0`, just off the
 * fraction the aggregator already computed rather than unit counts this screen no longer has.
 */
function stageOf(row: AcademyProgress): Exclude<Stage, 'all'> {
  if (row.percent === 0) return 'not-started';
  if (row.percent >= 1) return 'finished';
  return 'underway';
}

export default function AdminProgress() {
  const [course, setCourse] = useCourseParam();
  const [stage, setStage] = useState<Stage>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Open | null>(null);

  const courses = useAcademyCourses();

  const byKey = useMemo(() => {
    const map = new Map<string, AcademyCourse>();
    for (const entry of courses.all) map.set(entry.course_key, entry);
    return map;
  }, [courses.all]);

  // One request for the whole academy, not scoped to `course` — the picker below is a client-side
  // filter over this same list, same as the stage tabs and the search box, so changing it never
  // refetches. Matches Grades and Certificates, which made the same call for the same reason.
  const progress = useQuery({
    queryKey: ['academy-progress'],
    queryFn: () => listAcademyProgress({ pageSize: 200 }),
    staleTime: 60_000,
  });

  const all = progress.data?.results ?? [];

  /**
   * The courses worth offering HERE: the ones somebody is enrolled in, counted from the rows
   * already fetched rather than a separate enrolment list — `academy/progress` already answers
   * one row per active enrolment, so there is nothing left to fetch twice.
   *
   * Unlike Grades and Certificates, "has learners" IS the right test on this screen — a learner
   * sitting at 0% is exactly what it exists to surface, so a course with enrolments and no
   * activity is the opposite of a dead end.
   */
  const choices = useMemo(() => {
    const enrolled = new Map<string, number>();
    for (const row of all) enrolled.set(row.course_id, (enrolled.get(row.course_id) ?? 0) + 1);
    return courseChoices(
      courses.all,
      (entry) => {
        const count = enrolled.get(entry.course_key);
        return count ? `${count} learner${count === 1 ? '' : 's'}` : null;
      },
      course,
    );
  }, [all, courses.all, course]);

  const counts = useMemo(() => {
    const tally = { 'not-started': 0, underway: 0, finished: 0 };
    for (const row of all) tally[stageOf(row)] += 1;
    return tally;
  }, [all]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all
      .filter((row) => (course ? row.course_id === course : true))
      .filter((row) => (stage === 'all' ? true : stageOf(row) === stage))
      .filter((row) =>
        term
          ? row.username.toLowerCase().includes(term) || row.course_title.toLowerCase().includes(term)
          : true,
      )
      // Furthest along first. Ties go to whoever enrolled longest ago, which is what makes the
      // "Not started" tab read as "chase these, oldest first".
      .sort((a, b) => b.percent - a.percent || (a.since ?? '').localeCompare(b.since ?? ''));
  }, [all, course, stage, search]);

  const chosen = open ? all.find((row) => row.course_id === open.courseId && row.user_id === open.userId) : undefined;

  const columns: Column<AcademyProgress>[] = [
    { key: 'learner', header: 'Learner', render: (row) => row.username },
    // The course column earns its place only while the table spans courses: with one chosen it
    // is the same name on every row, and the detail panel names it too.
    ...(course
      ? []
      : [
          {
            key: 'course',
            header: 'Course',
            render: (row: AcademyProgress) => (
              <CourseCell course={byKey.get(row.course_id)} courseId={row.course_id} />
            ),
          },
        ]),
    {
      key: 'done',
      header: 'Units done',
      numeric: true,
      render: (row) => (
        <>
          {Math.round(row.percent * 100)}%
          <small className="admin-sub">
            {row.earned} of {row.possible} units
          </small>
        </>
      ),
    },
    {
      key: 'stage',
      header: 'State',
      render: (row) => {
        const state = stageOf(row);
        return (
          <Pill tone={state === 'finished' ? 'active' : state === 'underway' ? 'planned' : 'neutral'}>
            {state === 'finished' ? 'Finished' : state === 'underway' ? 'Underway' : 'Not started'}
          </Pill>
        );
      },
    },
    {
      key: 'since',
      header: 'Enrolled',
      render: (row) =>
        row.since ? (
          new Date(row.since).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        ) : (
          <span className="admin-muted">—</span>
        ),
    },
    {
      key: 'open',
      header: '',
      render: (row) => (
        <div className="admin__row-actions">
          <Button onClick={() => setOpen({ courseId: row.course_id, userId: row.user_id })}>Scores</Button>
        </div>
      ),
    },
  ];

  const loading = courses.isPending || progress.isPending;
  const learners = new Set(all.map((row) => row.username)).size;

  return (
    <>
      <PageHeader
        title="Progress"
        description="How far everyone has got through this academy's courses, and where they stopped."
      />

      <div className="admin-stack">
        <KpiRow>
          <Kpi
            label="Learners"
            value={loading ? '—' : learners}
            hint={all.length ? `${all.length} enrolments measured` : undefined}
          />
          <Kpi
            label="Finished"
            value={loading ? '—' : counts.finished}
            hint={counts.finished > 0 ? 'every unit complete' : undefined}
          />
          <Kpi label="Underway" value={loading ? '—' : counts.underway} />
          <Kpi
            label="Not started"
            value={loading ? '—' : counts['not-started']}
            hint={counts['not-started'] > 0 ? 'no unit opened yet' : undefined}
          />
        </KpiRow>

        {/* The detail sits above the list rather than below it: a table twenty-six rows long
            would otherwise push what you just asked for off the bottom of the screen. */}
        {open && chosen && (
          <LearnerDetail
            username={chosen.username}
            courseName={byKey.get(chosen.course_id)?.display_name ?? chosen.course_title}
            courseId={open.courseId}
            userId={open.userId}
            onClose={() => setOpen(null)}
          />
        )}

        <section className="admin-panel">
          <Toolbar>
            <Tabs
              label="Progress stage"
              active={stage}
              onChange={(id) => setStage(id as Stage)}
              tabs={[
                { id: 'all', label: 'Everyone', count: loading ? undefined : all.length },
                {
                  id: 'not-started',
                  label: 'Not started',
                  count: loading ? undefined : counts['not-started'],
                },
                { id: 'underway', label: 'Underway', count: loading ? undefined : counts.underway },
                { id: 'finished', label: 'Finished', count: loading ? undefined : counts.finished },
              ]}
            />

            <CourseSwitcher
              choices={choices}
              value={course}
              onChange={(value) => {
                setCourse(value);
                // The open learner belongs to the course you just left.
                setOpen(null);
              }}
              allMeasure={
                courses.isPending
                  ? 'Loading…'
                  : `${choices.length} course${choices.length === 1 ? '' : 's'} with learners`
              }
              hiddenNote={
                courses.all.length > choices.length
                  ? `${courses.all.length - choices.length} of this academy's ${courses.all.length} courses have no learners and are not listed.`
                  : undefined
              }
              disabled={courses.isPending}
              searchPlaceholder="Search courses with learners…"
            />

            <div className="toolbar__end">
              <SearchField value={search} onChange={setSearch} placeholder="Search learner or course" />
            </div>
          </Toolbar>

          {(courses.isError || progress.isError) && (
            <Banner tone="danger">
              <strong>We could not measure progress.</strong> Nothing has changed. Reload the page
              to try again — this needs staff access to the academy's courses.
            </Banner>
          )}

          {loading ? (
            <Spinner label="Measuring progress…" />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.course_id}|${row.user_id}`}
              empty={
                all.length === 0
                  ? 'Nobody is enrolled in this academy yet, so there is no progress to measure.'
                  : 'Nobody matches these filters.'
              }
            />
          )}

          {/*
            The old banner here reported a fixed cap this screen would not read past — 60
            enrolments academy-wide, because progress was one request per learner per course.
            Paging by enrolment removes that cap: the only way this list is incomplete now is the
            ordinary "there is another page", which `count` reports precisely, same as Grades.
          */}
          {progress.data && progress.data.count > progress.data.results.length && (
            <Banner tone="info">
              <strong>
                Showing {progress.data.results.length} of {progress.data.count} enrolments.
              </strong>{' '}
              Choose a course to measure all of its learners.
            </Banner>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * One learner's way through one course, fetched on demand.
 *
 * A SEPARATE REQUEST FROM THE LIST, deliberately: the bulk row above answers "how far", not
 * "with what grade" or "which sections" — those are the courseware-outline shape
 * `/api/course_home/v1/progress/{course}/{user_id}/` still answers, and fetching it for every row
 * on the page would be the one-request-per-learner fan-out this screen exists to remove. Asking
 * for it only when a row is opened keeps that cost at one request, for one learner, on demand.
 */
function LearnerDetail({
  username,
  courseName,
  courseId,
  userId,
  onClose,
}: {
  username: string;
  courseName: string;
  courseId: string;
  userId: number;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: ['learner-progress', courseId, userId],
    queryFn: () => getLearnerProgress(courseId, userId),
    staleTime: 60_000,
  });

  return (
    <div className="admin-stack">
      <section className="admin-panel">
        <header className="admin-panel__head">
          <h2>
            {username} <span className="admin-count">{courseName}</span>
          </h2>
          <div className="admin__row-actions">
            <Button onClick={onClose}>Close</Button>
          </div>
        </header>

        {query.isError && (
          <Banner tone="danger">
            <strong>We could not load this learner's scores.</strong> The course may not have
            opened yet, or the progress tab may be switched off for it. Nothing else on this page
            is affected.
          </Banner>
        )}

        {query.isPending ? <Spinner label="Loading scores…" /> : query.data ? <Scores data={query.data} /> : null}
      </section>
    </div>
  );
}

/** The courseware-outline detail one learner's progress carries: grade, certificate, sections. */
function Scores({ data }: { data: LearnerProgress }) {
  const completion = data.completion_summary;
  const share = completionShare(completion);
  const grade = data.course_grade;

  return (
    <>
      <div className="admin__row-actions">
        {data.enrollment_mode ? <Pill tone="neutral">{data.enrollment_mode}</Pill> : null}
      </div>

      <KpiRow>
        <Kpi
          label="Units complete"
          value={`${completion?.complete_count ?? 0}`}
          hint={share === null ? undefined : `${Math.round(share * 100)}% of the course`}
        />
        <Kpi label="Still to do" value={`${completion?.incomplete_count ?? 0}`} />
        <Kpi
          label="Grade"
          value={`${asPercent(grade?.percent) ?? 0}%`}
          hint={grade?.letter_grade ? `Letter grade ${grade.letter_grade}` : undefined}
        />
        <Kpi label="Passing" value={grade?.is_passing ? 'Yes' : 'Not yet'} />
      </KpiRow>

      {/*
        Locked content is called out separately rather than folded into "still to do": it is
        content this learner CANNOT reach — gated behind a prerequisite or an upgrade — so
        counting it against them would misrepresent both them and the course.
      */}
      {completion && completion.locked_count > 0 && (
        <Banner tone="info">
          <strong>{completion.locked_count} units are locked for this learner.</strong> They are
          gated behind a prerequisite or a paid track, so they are not counted as outstanding work.
        </Banner>
      )}

      {data.certificate_data?.cert_status && (
        <Banner tone={data.certificate_data.cert_status === 'downloadable' ? 'success' : 'info'}>
          <strong>Certificate: {data.certificate_data.cert_status.replace(/_/g, ' ')}.</strong>{' '}
          {data.certificate_data.cert_web_view_url ? (
            // The platform's own certificate page, proxied under this host: a plain link, never
            // a router one.
            <a href={data.certificate_data.cert_web_view_url}>View it</a>
          ) : (
            'Not available to view yet.'
          )}
        </Banner>
      )}

      <header className="admin-panel__head">
        <h2>Scores by section</h2>
      </header>

      {(data.section_scores ?? []).length === 0 ? (
        <p className="admin-muted">This course has no graded work.</p>
      ) : (
        <ul className="admin-sections">
          {data.section_scores.map((section) => (
            <li key={section.display_name}>
              <h3>{section.display_name}</h3>
              <ul>
                {section.subsections.map((subsection) => {
                  const graded = subsection.has_graded_assignment;
                  return (
                    <li key={subsection.display_name}>
                      <span>{subsection.display_name}</span>
                      {graded ? (
                        <b>
                          {subsection.num_points_earned}/{subsection.num_points_possible}
                          {subsection.assignment_type ? (
                            <small> · {subsection.assignment_type}</small>
                          ) : null}
                        </b>
                      ) : (
                        <small className="admin-muted">Not graded</small>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
